import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getBackendUrl } from '../../utils/backendUrl';
import { validatePassword, calculatePasswordStrength, getPasswordStrengthLabel, getPasswordRecommendations, isCommonPassword } from '../../utils/passwordValidation';
import ModalCloseButton from '../ui/ModalCloseButton';

const AuthModal = ({ mode, onClose, onSwitchMode, onSuccess, sidebarOffset = 0, isSessionExpired = false }) => {
  // Safely get auth functions - might not be available if used outside provider
  let login, register;
  try {
    const auth = useAuth();
    login = auth.login;
    register = auth.register;
  } catch (error) {
    // If used outside AuthProvider, provide fallback functions
    login = async () => { throw new Error('Auth not available'); };
    register = async () => { throw new Error('Auth not available'); };
  }
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isSignUp = mode === 'signup';

  // Real-time password match validation
  const passwordMatchStatus = useMemo(() => {
    if (!isSignUp) return null;
    if (!confirmPassword) return null; // Don't show status until user starts typing
    if (password === confirmPassword) {
      return { match: true, message: 'Passwords match' };
    }
    return { match: false, message: 'Passwords do not match' };
  }, [password, confirmPassword, isSignUp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Basic validation - check each required field specifically
      if (isSignUp && !name) {
        setError('Name is required.');
        setLoading(false);
        return;
      }
      if (!email) {
        setError('Email is required.');
        setLoading(false);
        return;
      }
      if (!email.includes('@')) {
        setError('Please enter a valid email address.');
        setLoading(false);
        return;
      }
      if (!password) {
        setError('Password is required.');
        setLoading(false);
        return;
      }
      if (isSignUp && !confirmPassword) {
        setError('Please confirm your password.');
        setLoading(false);
        return;
      }

      // Validate password using industry standards
      const passwordValidation = validatePassword(password, isSignUp ? confirmPassword : null);
      if (!passwordValidation.valid) {
        // If mismatch is the only validation issue
        const isMismatchOnly = passwordValidation.errors.length === 1 && passwordValidation.errors[0] === 'Passwords do not match';
        if (isMismatchOnly) {
          setError('Please fix the highlighted fields.');
        } else {
          setError('Please choose a password that meets the required items.');
        }
        setLoading(false);
        return;
      }

      // Call API
      if (isSignUp) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }

      // Success - proceed to app
      setLoading(false);
      onSuccess();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      // TODO: Implement Google OAuth flow
      // For now, redirect to OAuth endpoint
      window.location.href = `${getBackendUrl()}/api/auth/oauth/google`;
    } catch (err) {
      setError('Google sign-in is not yet available. Please use email/password.');
      setLoading(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 ${isSessionExpired ? 'bg-gray-50' : 'bg-black/50 backdrop-blur-md'} flex items-start sm:items-center justify-center z-[70] p-4 overflow-y-auto py-6 sm:py-8`}
      style={sidebarOffset ? { paddingLeft: `${sidebarOffset}px` } : undefined}
      onClick={isSessionExpired ? undefined : onClose}
    >
      <div
        className={`bg-white rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.12)] border border-gray-100/80 w-full relative my-auto p-5 sm:p-8 animate-fade-in-up duration-300 ${
          isSignUp ? 'max-w-md md:max-w-[780px]' : 'max-w-md'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Session Expired Notice */}
        {isSessionExpired && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-xl text-xs sm:text-sm text-orange-800 text-center font-medium">
            Your session expired after inactivity, so the workspace is hidden for privacy. Sign in again to continue.
          </div>
        )}

        {/* Close Button */}
        {!isSessionExpired && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-5 top-5 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {isSignUp ? (
          <div className="flex flex-col md:flex-row gap-8 items-stretch">
            {/* Left Column: Form, Header & OAuth */}
            <div className="flex-1 min-w-0 space-y-4">
              {/* Header */}
              <div className="text-center mb-4">
                <div className="mx-auto flex justify-center mb-2">
                  <img src="/new_icon+text_color.png" alt="CraftMyParty" className="h-9 sm:h-10 w-auto object-contain" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                  Create Your Account
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                  Start planning amazing parties
                </p>
              </div>

              {/* Google Sign In */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full h-11 flex items-center justify-center gap-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50/80 hover:border-gray-300 active:scale-[0.99] transition-all duration-150 shadow-sm disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-4">
                <div className="flex-1 h-px bg-gray-100"></div>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full h-11 px-4 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full h-11 px-4 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full h-11 px-4 pr-10 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Mobile-only Checklist */}
                  {password && (
                    <div className="block md:hidden text-left mt-3">
                      <PasswordStrengthIndicator password={password} />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className={`w-full h-11 px-4 pr-16 bg-gray-50/50 border rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-4 transition-all duration-150 outline-none ${
                        passwordMatchStatus?.match === true
                          ? 'border-emerald-500 focus:ring-emerald-500/10 focus:border-emerald-500'
                          : passwordMatchStatus?.match === false
                          ? 'border-rose-500 focus:ring-rose-500/10 focus:border-rose-500'
                          : 'border-gray-200 focus:ring-purple-500/10 focus:border-purple-500'
                      }`}
                    />
                    {/* Password match indicator icon */}
                    {passwordMatchStatus && (
                      <div className={`absolute right-10 top-1/2 -translate-y-1/2 ${passwordMatchStatus.match ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {passwordMatchStatus.match ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Password match status message */}
                  {passwordMatchStatus && (
                    <p className={`mt-1.5 text-xs flex items-center gap-1.5 font-medium ${passwordMatchStatus.match ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {passwordMatchStatus.match ? (
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span>{passwordMatchStatus.message}</span>
                    </p>
                  )}
                </div>

                {error && (
                  <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-xs text-rose-600 font-medium">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white text-sm sm:text-base font-bold rounded-xl shadow hover:shadow-md active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Please wait...
                    </span>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>

              {/* Switch Mode */}
              <p className="text-center text-xs sm:text-sm text-gray-600 mt-4">
                Already have an account?{' '}
                <button
                  onClick={() => onSwitchMode('signin')}
                  className="text-zinc-900 font-bold hover:text-zinc-700 transition-colors"
                >
                  Sign In
                </button>
              </p>

              {/* Terms */}
              <p className="text-center text-[10px] sm:text-xs text-gray-400 mt-3 leading-normal">
                By signing up, you agree to our{' '}
                <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-zinc-900 hover:underline font-medium">
                  Terms
                </a>{' '}
                and{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-zinc-900 hover:underline font-medium">
                  Privacy Policy
                </a>
              </p>
            </div>

            {/* Right Column: Statically visible Checklist on Desktop (Centered vertically) */}
            <div className="hidden md:flex flex-col justify-center w-[300px] border-l border-gray-100 pl-6 flex-shrink-0">
              <PasswordStrengthIndicator password={password} />
            </div>
          </div>
        ) : (
          // Sign In mode remains simple, centered, and max-w-md
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="mx-auto flex justify-center mb-3">
                <img src="/new_icon+text_color.png" alt="CraftMyParty" className="h-9 sm:h-10 w-auto object-contain" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                Welcome Back
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                Sign in to continue planning
              </p>
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full h-11 flex items-center justify-center gap-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50/80 hover:border-gray-300 active:scale-[0.99] transition-all duration-150 shadow-sm disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 h-px bg-gray-100"></div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-gray-100"></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full h-11 px-4 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-11 px-4 pr-10 bg-gray-50/50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:bg-white focus:border-zinc-900 focus:ring-4 focus:ring-zinc-900/5 transition-all outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 01-1.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-xs text-rose-600 font-medium">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white text-sm sm:text-base font-bold rounded-xl shadow hover:shadow-md active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Please wait...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <p className="text-center text-xs sm:text-sm text-gray-600 mt-4">
              Don't have an account?{' '}
              <button
                onClick={() => onSwitchMode('signup')}
                className="text-zinc-900 font-bold hover:text-zinc-700 transition-colors"
              >
                Sign Up
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }) => {
  const strength = useMemo(() => calculatePasswordStrength(password), [password]);
  const { label, color } = getPasswordStrengthLabel(strength);
  const validation = useMemo(() => validatePassword(password), [password]);

  const requiredItems = useMemo(() => {
    return [
      {
        id: 'length',
        text: 'At least 8 characters',
        met: password.length >= 8
      },
      {
        id: 'not_common',
        text: 'Avoid common words/patterns',
        met: password.length > 0 && !isCommonPassword(password)
      }
    ];
  }, [password]);

  const recommendedItems = useMemo(() => {
    return [
      {
        id: 'case',
        text: 'Uppercase & lowercase letters',
        met: /[a-z]/.test(password) && /[A-Z]/.test(password)
      },
      {
        id: 'number',
        text: 'At least one number',
        met: /[0-9]/.test(password)
      },
      {
        id: 'special',
        text: 'At least one special character',
        met: /[^a-zA-Z0-9]/.test(password)
      }
    ];
  }, [password]);

  const allRequiredMet = useMemo(() => {
    return requiredItems.every((item) => item.met);
  }, [requiredItems]);

  return (
    <div className="space-y-4 text-left">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.249-8.25-3.286zm0 3.036h.008v.008H12V8.25z" />
        </svg>
        <span className="text-xs font-bold text-gray-800 uppercase tracking-wider">Password Strength</span>
      </div>
      
      {/* Unified Card */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4 shadow-sm">
        {/* Strength Bar */}
        {password ? (
          <div className="space-y-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500 font-medium">Strength Rating:</span>
              <span 
                className="font-bold px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider transition-all duration-200" 
                style={{ color, backgroundColor: `${color}15` }}
              >
                {label}
              </span>
            </div>
            
            {/* Multi-segment Progress Bar */}
            <div className="grid grid-cols-4 gap-1.5 h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-300" style={{ backgroundColor: strength >= 25 ? color : '#E5E7EB' }} />
              <div className="h-full rounded-full transition-all duration-300" style={{ backgroundColor: strength >= 50 ? color : '#E5E7EB' }} />
              <div className="h-full rounded-full transition-all duration-300" style={{ backgroundColor: strength >= 75 ? color : '#E5E7EB' }} />
              <div className="h-full rounded-full transition-all duration-300" style={{ backgroundColor: strength >= 90 ? color : '#E5E7EB' }} />
            </div>

            <div className="text-[11px] font-medium leading-tight">
              {allRequiredMet ? (
                <p className="text-emerald-600 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Meets minimum requirements
                </p>
              ) : (
                <p className="text-rose-500 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  Requires adjustments
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-[11px] font-medium text-gray-500 leading-normal flex items-center gap-1.5 py-1">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Enter a password to check strength
          </div>
        )}
 
        {/* Checklist */}
        <div className="space-y-3 pt-3 border-t border-gray-100">
          <div>
            <p className="font-bold text-gray-400 text-[10px] uppercase tracking-wider mb-2">Required Items</p>
            <ul className="space-y-1.5">
              {requiredItems.map((req) => (
                <li key={req.id} className="flex items-center gap-2.5 text-xs">
                  {req.met ? (
                    <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-200 flex-shrink-0" />
                  )}
                  <span className={req.met ? 'text-gray-700 font-medium' : 'text-gray-400'}>
                    {req.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
 
          <div className="border-t border-gray-100 pt-3">
            <p className="font-bold text-gray-400 text-[10px] uppercase tracking-wider mb-2">Recommendations</p>
            <ul className="space-y-1.5">
              {recommendedItems.map((req) => (
                <li key={req.id} className="flex items-center gap-2.5 text-xs">
                  {req.met ? (
                    <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0" />
                  )}
                  <span className={req.met ? 'text-gray-700 font-medium' : 'text-gray-400'}>
                    {req.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
 
      {/* Warnings */}
      {password && validation.warnings.length > 0 && !allRequiredMet && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2">
          <svg className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="leading-tight text-[11px] sm:text-xs font-medium">{validation.warnings[0]}</span>
        </div>
      )}
    </div>
  );
};

export default AuthModal;
